import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OutletService } from '../../services';
import { handleNucleusResponse } from '../../services/utils';
import type { CreateOutletRequest, UpdateOutletRequest } from '@billx/types';
import { getTokenManager } from '../../config/tokenManager';
import {
  deleteQueryItem,
  updateQueryItem,
  upsertQueryItem,
} from '@billx/utils';

import { useCurrentBusiness } from '../businesses/useCurrentBusiness';

export const useOutlets = (businessId: string | undefined) => {
  const queryClient = useQueryClient();

  const QUERY_KEY = ['outlets', businessId];
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      if (!businessId) {
        throw new Error('No business id provided');
      }
      const response = await OutletService.fetchOutletsByBusinessId(businessId);
      return handleNucleusResponse(response).data;
    },
    enabled: !!businessId,
  });

  const createOutlet = useMutation({
    mutationFn: (data: CreateOutletRequest) => OutletService.createOutlet(data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, upsertQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateOutlet = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOutletRequest }) =>
      OutletService.updateOutlet(id, data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, updateQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const deleteOutlet = useMutation({
    mutationFn: (id: string) => OutletService.deleteOutlet(id),
    onSuccess: (response, id) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, deleteQueryItem(id));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const { data: outlets = [], ...restQuery } = query;
  return {
    // Query data
    outlets,
    ...restQuery,
    // Mutations
    createOutlet,
    updateOutlet,
    deleteOutlet,
  };
};

export const useCurrentBusinessOutlets = () => {
  const { currentBusiness, isLoading: businessLoading } = useCurrentBusiness();

  const { isLoading, ...rest } = useOutlets(currentBusiness?.id);
  return { isLoading: isLoading || businessLoading, ...rest };
};
